import editorialStyle from "../../../components/editorial/style.scss";
import expandableStyle from "../../../components/expandable/style.scss";

import { Fragment } from "preact";
import Link, {
  Button,
  ButtonList,
  ButtonBar,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import FixedTable from "../../../components/fixed-table";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import Tags from "../../../components/tags";
import Image from "../../../components/image";
import ImageSet from "../../../components/image-set";
import Author from "../../../components/author";
import BluettiCouponCode from "../components/coupon-code";
import NoWrap from "../../../components/no-wrap";
import Expandable from "../../../components/expandable";
import Accordion from "../../../components/accordion";
import AccordionPreview from "../../../components/accordion/preview";
import SlugHeading from "../../../components/slug-heading";
import SlugLink from "../../../components/slug-link";
import PhotoListItem from "../../../components/photo-list-item";
import ReviewItem from "../../../components/review-item";
import Questions from "../../../components/questions";
import { lookupByUrl } from "../../../settings/route-data-lookup";
import { mergeClasses } from "../../../components/helpers";
import namedRoutes from "../../../settings/routes";

import powerStationData from "../../../data/power-stations/bluetti";
import { getGroupedData } from "../../power-systems/dataHelper";
import DataView from "../../power-systems/dataView";
const highlightedProducts = ["Apex 200", "Apex 300", "Elite 300", "RV5"];
const groupedData = getGroupedData(powerStationData, {
  overrideProp: "highlighted",
  overrides: highlightedProducts,

  getGroupName: (item) => {
    return "allSystems";
  },
  groups: ["allSystems"],
});

const headerInfo = lookupByUrl(namedRoutes.bluetti.vanlife);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      positionY={10}
      background="/images/backgrounds/bluetti.webp"
      titlePrefix="The Ultimate Guide to"
      title="BLUETTI Power Systems"
      subTitle="For Vanlife, RVs & Overlanding"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <p>
          <strong>
            BLUETTI power systems are a strong choice for vanlife, RVs, and
            overlanding, offering reliable off‑grid power without the complexity
            of a full DIY electrical build.
          </strong>{" "}
          This guide covers the best BLUETTI power stations, alternator
          chargers, solar options, and accessories, with clear recommendations
          for setups ranging from budget‑friendly to high‑capacity off‑grid
          builds.
        </p>

        <AffiliateDisclosure />

        <Accordion
          items={[
            {
              heading: "Table of Contents",
              content: (
                <ol>
                  <li>
                    <SlugLink>Power Stations</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>Top Models Comparison</SlugLink>
                      </li>
                      <li>
                        <SlugLink>All Power Systems</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>Alternator Chargers</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Solar Power</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Shore Power</SlugLink>
                  </li>
                </ol>
              ),
            },
            {
              heading: "Terminology",
              content: (
                <Fragment>
                  <p>
                    <strong>AC power</strong> (alternating current) is the type
                    of electricity found in household outlets, allowing you to
                    run standard appliances such as a toaster, induction
                    cooktop, computer, etc.
                  </p>
                  <p>
                    <strong>Alternator</strong> is a device that powers a
                    vehicle’s electrical systems and charges the battery while
                    the engine runs.
                  </p>
                  <p>
                    <strong>Amperage</strong> (Amps, A) measures the rate of
                    electric current flow.
                  </p>
                  <p>
                    <strong>DC power</strong> (direct current) is the
                    electricity that your vehicle, BLUETTI devices, and most of
                    your essential accessories like fridges, fans, heaters, and
                    lights will use.
                  </p>
                  <p>
                    <strong>Solar array</strong> is a group of solar panels that
                    work together to convert sunlight into electricity.
                  </p>
                  <p>
                    <strong>Volts</strong> (V) measure the electric potential
                    difference between two points, indicating how strongly
                    electricity is pushed through a circuit.
                  </p>
                  <p>
                    <strong>Watts</strong> (W) are calculated by multiplying
                    volts by amps.
                  </p>
                  <p>
                    <strong>Watt-hours</strong> (Wh) and{" "}
                    <strong>kilowatt-hours</strong> (kWh) measure total energy
                    used or produced over time.
                  </p>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Power Stations</SlugHeading>
        <p>
          BLUETTI power stations offer a modern alternative to DIY electrical
          systems, reducing installation time and overall cost. These are the
          four best BLUETTI models to consider for vanlife, RVs, and off‑grid
          travel.
        </p>

        <ImageSet
          size={4}
          images={[
            {
              lg: "/images/diagrams/power-systems/bluetti/bluetti-apex-200.webp?v=2",
              alt: "BLUETTI Apex 200 vanlife system diagram",
            },
            {
              lg: "/images/diagrams/power-systems/bluetti/bluetti-apex-300.webp?v=2",
              alt: "BLUETTI Apex 300 vanlife system diagram",
            },
            {
              lg: "/images/diagrams/power-systems/bluetti/bluetti-elite-300.webp?v=2",
              alt: "BLUETTI Elite 300 vanlife system diagram",
            },
            {
              lg: "/images/diagrams/power-systems/bluetti/bluetti-rv5.webp?v=2",
              alt: "BLUETTI RV5 vanlife system diagram",
            },
          ]}
        />

        <BluettiCouponCode
          href="https://www.bluettipower.com/"
          buttonText="BLUETTI Store"
        />

        <br />
        <SlugHeading tag="h3">Top Models Comparison</SlugHeading>
        <p>
          A side‑by‑side comparison of the top BLUETTI picks, sorted by battery
          capacity:
        </p>
        <Expandable expandText="Show Hidden Rows">
          <FixedTable>
            <table>
              <thead>
                <tr>
                  <th class={editorialStyle.sticky}>Model</th>
                  <th>Apex 200</th>
                  <th>Apex 300</th>
                  <th>Elite 300</th>
                  <th>RV5</th>
                </tr>
              </thead>
              <tbody>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Photo</strong>
                  </th>
                  <td>
                    <Image
                      src="/images/products/bluetti/apex-200/front.webp"
                      alt="BLUETTI AC200L"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                  <td>
                    <Image
                      src="/images/products/bluetti/apex-300/front.webp"
                      alt="BLUETTI Elite 300"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                  <td>
                    <Image
                      src="/images/products/bluetti/elite-300/front.webp"
                      alt="BLUETTI Apex 300"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                  <td>
                    <Image
                      src="/images/products/bluetti/rv5/rv5_b4810_epanel_epad.webp"
                      alt="BLUETTI RV5"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                </tr>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Links</strong>
                  </th>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/pages/apex-200-expandable-power-station"
                        icon="Bluetti"
                      >
                        Store
                      </Button>
                      {/* <Button
                        size="small"
                        external
                        href="https://cdn.shopify.com/s/files/1/0536/3390/8911/files/17.0303.0627-02_AC200L_User_Manual_BLUETTI_PPS011-6D01A2_23.12.06.pdf?v=1702544515"
                        secondary
                      >
                        Manual
                      </Button> */}
                    </ButtonBar>
                  </td>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/apex-300-home-battery-backup?id=68804e7e63e18b529ef2604a&variant=47351562174683"
                        icon="Bluetti"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://s4.bluettipower.com/bluetti/purchasePageBiz/2025/12/Apex%20300%20User%20%20Manual%20US%20EN-FR-1764555919287-cde9.pdf"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/elite-300-portable-power-station"
                        icon="Bluetti"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://s4.bluettipower.com/bluetti/purchasePageBiz/2026/03/Elite%20300%20User%20Manual%20US%20EN-FR-1772623629263-39f7.pdf"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/rv5-rv-solar-system?id=68da7c684197d774e9a80362&variant=47637171503323"
                        icon="Bluetti"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://s4.bluettipower.com/bluetti/purchasePageBiz/2025/09/RV5%C2%A0User%20Manual%20US%20EN-1759160161231-e9c8.pdf"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Price</strong>
                  </th>
                  <td>
                    <NoWrap>
                      $1,999 <s>TBD</s>
                    </NoWrap>
                  </td>
                  <td>
                    <NoWrap>
                      $1,699 <s>$2,199</s>
                    </NoWrap>
                  </td>
                  <td>
                    <NoWrap>
                      $1,199 <s>$2,299</s>
                    </NoWrap>
                  </td>
                  <td>
                    <NoWrap>
                      $3,999 <s>$4,799</s>
                    </NoWrap>
                  </td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Capacity</strong>
                  </th>
                  <td>2,560Wh</td>
                  <td>2,764Wh</td>
                  <td>3,014Wh</td>
                  <td>5,120Wh+</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Extra battery</strong>
                  </th>
                  <td>6x</td>
                  <td>6x</td>
                  <td>--</td>
                  <td>24x</td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>AC output</strong>
                  </th>
                  <td>
                    3,200W total <br />
                    3,600W surge
                  </td>
                  <td>
                    3,840W total <br />
                    7,680W surge
                  </td>
                  <td>
                    2,400W total <br />
                    4,800W surge
                  </td>
                  <td>5,000W total</td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>AC ports</strong>
                  </th>
                  <td>
                    x3 (20A each) <br />
                    x1 (30A)
                  </td>
                  <td>
                    x4 (20A each) <br />
                    x1 (30A) <br />
                    x1 (120/240V, 50A)
                  </td>
                  <td>
                    x4 (20A each) <br />
                    x1 (30A) <br />
                  </td>
                  <td>≤ 8 circuits</td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>DC output</strong>
                  </th>
                  <td>12V⎓30A</td>
                  <td>
                    Add-on <sup>1</sup> <sup>2</sup>
                  </td>
                  <td>12V⎓30A</td>
                  <td>12V⎓100A / 24V⎓60A</td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Solar input 1</strong>
                  </th>
                  <td>
                    60–280V⎓16A <br />
                    2,000W
                  </td>
                  <td>
                    12-60V⎓20A <br />
                    1,200W
                  </td>
                  <td>
                    12-60V⎓22A <br />
                    1,200W
                  </td>
                  <td>
                    12-50V⎓50A <br />
                    1,800W
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Solar input 2</strong>
                  </th>
                  <td>
                    12–60V⎓25A <br />
                    1,000W
                  </td>
                  <td>
                    12-60V⎓20A <br />
                    1,200W
                  </td>
                  <td>--</td>
                  <td>
                    12-50V⎓50A <br />
                    1,800W
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Alternator input</strong>
                  </th>
                  <td>1,200W</td>
                  <td>1,200W</td>
                  <td>1,200W</td>
                  <td>
                    600W (12V) <br />
                    1,200W (24V)
                  </td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>AC input</strong>
                  </th>
                  <td>TBD</td>
                  <td>15A 1,800W</td>
                  <td>15A 1,800W</td>
                  <td>42A 5,000W</td>
                </tr>

                <tr
                  class={mergeClasses([
                    editorialStyle.rowBorder,
                    expandableStyle.expandableHide,
                  ])}
                >
                  <th class={editorialStyle.sticky}>
                    <strong>USB C</strong>
                  </th>
                  <td>2x</td>
                  <td>
                    Add-on <sup>1</sup> <sup>2</sup>
                  </td>
                  <td>2x</td>
                  <td>--</td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>USB A</strong>
                  </th>
                  <td>2x</td>
                  <td>
                    Add-on <sup>1</sup> <sup>2</sup>
                  </td>
                  <td>2x</td>
                  <td>--</td>
                </tr>

                <tr
                  class={mergeClasses([
                    editorialStyle.rowBorder,
                    expandableStyle.expandableHide,
                  ])}
                >
                  <th class={editorialStyle.sticky}>
                    <strong>Size (in)</strong>
                  </th>
                  <td>20.9 × 12.8 × 8.3"</td>
                  <td>20.7 × 12.9 × 12.6</td>
                  <td>14.4 × 12 × 11.7</td>
                  <td>Scalable</td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>Size (mm)</strong>
                  </th>
                  <td>
                    <NoWrap>530 × 326 × 211 mm</NoWrap>
                  </td>
                  <td>
                    <NoWrap>525 × 327 × 320</NoWrap>
                  </td>
                  <td>
                    <NoWrap>366 × 305 × 297</NoWrap>
                  </td>
                  <td>Scalable</td>
                </tr>
              </tbody>
            </table>
          </FixedTable>
          <div class={expandableStyle.expandableHide}>
            <p class="caption">
              <strong>1.</strong> Requires BLUETTI{" "}
              <Link
                affiliateData={affiliateMerchants.bluetti}
                href="https://www.bluettipower.com/products/hub-d1-parallel-box"
              >
                Hub D1
              </Link>
              . <strong>2.</strong> Requires BLUETTI{" "}
              <Link
                affiliateData={affiliateMerchants.bluetti}
                href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
              >
                DC Hub
              </Link>{" "}
              and{" "}
              <Link
                affiliateData={affiliateMerchants.bluetti}
                href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
              >
                Charger 2
              </Link>{" "}
              connected via bidirectional port.
            </p>
          </div>
        </Expandable>

        <h4>My Reviews</h4>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.bluetti.apex200} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.rv5} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.ac200l} />
          </li>
        </ul>
        <br />

        <SlugHeading tag="h3">All Power Systems</SlugHeading>
        <DataView data={groupedData.allSystems} />

        <p>
          <strong>See how other power systems compare:</strong>
        </p>
        <ReviewItem href={namedRoutes.powerSystems} />
      </Center>
      <Center>
        <SlugHeading tag="h2">Alternator Chargers</SlugHeading>
        <p>
          Alternator charging is essential for vanlife, offering fast, reliable
          recharging while driving. It often outperforms solar and isn’t
          dependent on sunshine or good weather. BLUETTI currently offers two
          alternator chargers:{" "}
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/bluetti-charger-1"
          >
            Charger 1
          </Link>{" "}
          and{" "}
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
          >
            Charger 2
          </Link>
          . These units pull power from your vehicle to charge your power
          station, similar to a DC-DC charger.
        </p>

        <Fragment>
          <FixedTable>
            <table>
              <thead>
                <tr>
                  <th class={editorialStyle.sticky}>Model</th>
                  <th>Charger 2</th>
                  <th>Charger 1</th>
                </tr>
              </thead>
              <tbody>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Photo</strong>
                  </th>
                  <td>
                    <Image
                      src="/images/products/bluetti/charger-2/front.webp"
                      alt="BLUETTI Charger 2"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                  <td>
                    <Image
                      src="/images/products/bluetti/charger-1/front.webp"
                      alt="BLUETTI Charger 1"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                </tr>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Links</strong>
                  </th>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
                        icon="Bluetti"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://cdn.shopify.com/s/files/1/0550/5620/3836/files/CHARGER_2_User_Manual_EN.pdf?v=1768545688"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.bluetti}
                        href="https://www.bluettipower.com/products/bluetti-charger-1"
                        icon="Bluetti"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://s4.bluettipower.com/bluetti_lgf/support/2025/05/4a4f1b84-aa5a-4563-8e3f-28f5c3053576.pdf"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Price</strong>
                  </th>
                  <td>
                    <NoWrap>
                      $499 <s>$599</s>
                    </NoWrap>
                  </td>
                  <td>
                    $249 <s>$399</s>
                  </td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Alternator Input</strong>
                  </th>
                  <td>800W</td>
                  <td>560W</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Solar Input</strong>
                  </th>
                  <td>13-50V⎓20A, 600W</td>
                  <td>--</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Power Station Port</strong>
                  </th>
                  <td>1,200W</td>
                  <td>560W</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Bidirectional Port</strong>
                  </th>
                  <td>
                    1,000W <sup>1</sup>
                  </td>
                  <td>--</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>DC Hub Port</strong>
                  </th>
                  <td>
                    700W <sup>2</sup>
                  </td>
                  <td>--</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Max Reverse Charge</strong>
                  </th>
                  <td>
                    800W <sup>1</sup>
                  </td>
                  <td>--</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Battery Maintenance</strong>
                  </th>
                  <td>
                    100W <sup>1</sup>
                  </td>
                  <td>--</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Signal Wire</strong> (D+)
                  </th>
                  <td>Add-on</td>
                  <td>--</td>
                </tr>
              </tbody>
            </table>
          </FixedTable>
          <p class="caption">
            <strong>1.</strong> Requires compatible BLUETTI product.{" "}
            <strong>2.</strong> Requires BLUETTI{" "}
            <Link
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
            >
              DC Hub
            </Link>
            . Also requires a BLUETTI device connected to the bidirectional port
            to be active when vehicle is off.
          </p>
        </Fragment>

        <h3>My Reviews</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.bluetti.charger2.review} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.charger1.review} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.altCharger.transit} />
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">Solar Power</SlugHeading>
        <p>
          Solar charging offers a quiet, sustainable, and free way to extend
          off-grid time. However, its effectiveness depends entirely on
          sunlight; factors like shade, sun angle, temperature, and wiring
          configuration (series, parallel, or series-parallel) all significantly
          impact power output.
        </p>

        <SlugHeading tag="h3">Solar Array Configuration</SlugHeading>
        <p>
          Solar panel setups often involve arrays of multiple panels using
          series, parallel, or series-parallel wiring. Series connections add
          voltage but can suffer significant power drops if even one panel is
          partially shaded. Parallel wiring adds current (amperage), allowing
          unshaded panels to still produce power, ideal for patchy sunlight.
          Series-parallel setups combine both approaches.
        </p>
        <Accordion
          headingsAs="h4"
          items={[
            {
              heading: "Calculating Solar Array Output",
              content: (
                <Fragment>
                  <p>
                    To calculate the total voltage and amperage of your solar
                    array, you'll use the Open Circuit Voltage (Voc) and Short
                    Circuit Current (Isc) listed on your panels.
                  </p>
                  <p>For panels wired in series:</p>
                  <p style="padding:0.6em 1em; background: #ddd; border-radius: 6px;">
                    Total Array Voltage = Voc_panel1 + Voc_panel2 + ...
                    <br />
                    Total Array Amperage = Isc_of_a_single_panel
                  </p>
                  <p>For panels wired in parallel:</p>
                  <p style="padding:0.6em 1em; background: #ddd; border-radius: 6px;">
                    Total Array Voltage = Voc_of_a_single_panel
                    <br />
                    Total Array Amperage = Isc_panel1 + Isc_panel2 + ...
                  </p>
                  <p>
                    In a series-parallel setup, you'll first calculate the
                    voltage of each series string, then add the current of those
                    strings together if they're connected in parallel.
                  </p>
                </Fragment>
              ),
            },
          ]}
        />

        <SlugHeading tag="h3">Solar Panel Types</SlugHeading>
        <p>
          BLUETTI offers{" "}
          <Link
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/collections/solar-panels"
          >
            portable solar panels
          </Link>
          , but you may prefer rigid panels for a permanent roof mount.
        </p>

        <Accordion
          headingsAs="h4"
          items={[
            {
              heading: "Types of Solar Panels",
              content: (
                <Fragment>
                  <p>
                    <strong>Mountable vs portable:</strong> Mountable solar
                    panels provide a semi-permanent and typically higher-output
                    charging solution affixed to the roof, whereas portable
                    solar panels offer the flexibility to chase the sun and can
                    be stowed away when not in use or during travel.
                  </p>
                  <p>
                    <strong>Rigid vs flexible:</strong> Rigid solar panels, with
                    their thicker framed design, offer greater durability and
                    efficiency and are common for roof installations. Flexible
                    panels are much thinner, lighter‑weight, and can conform to
                    a curved surface for stealthier installations.
                  </p>
                  <p>
                    <strong>Bifacial vs monofacial:</strong> Bifacial solar
                    panels gather light from both sides for potentially higher
                    energy yield than traditional monofacial panels.
                  </p>
                  <p>
                    <strong>Monocrystalline vs polycrystalline:</strong>{" "}
                    Monocrystalline panels are more efficient and therefore
                    space-saving, ideal for limited RV/van roof space, while
                    older polycrystalline panels can be less expensive but
                    aren’t recommended.
                  </p>
                </Fragment>
              ),
            },
          ]}
        />

        <p>
          While BLUETTI provides various solar panel options and bundles,
          prioritize panels that physically fit your vehicle; using other
          reputable brands is okay. I personally use{" "}
          <Link external href="https://www.newpowa.com/">
            Newpowa
          </Link>{" "}
          panels as they fit my rack setup best.
        </p>

        <SlugHeading tag="h3">Accessories for Solar Panels</SlugHeading>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/mc4-solar-y-branch-parallel-cable?variant=48179097043163"
              img="/images/products/bluetti/solar-parallel-connection-cable.webp"
              title="Solar Parallel Connection Cable"
              description={
                <Fragment>Connects solar panels in parallel</Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">Shore Power</SlugHeading>
        <p>
          Shore power charging allows you to replenish your BLUETTI power
          station by plugging into a standard electrical outlet, often found at
          campgrounds or homes, using the power station's normal AC charging
          cable. For easier charging, you can install a weather-resistant AC
          port on your vehicle, such as the{" "}
          <Link
            affiliate="https://amzn.to/4juX4cb"
            href="https://www.amazon.com/dp/B009ANV81S"
          >
            Noco GCP1
          </Link>
          .
        </p>
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.ecoflow.vanlife} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.alternatorChargers} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
    </Editorial>
  </div>
);

export default Page;
